import { EyeIcon, FileTextIcon, PaperclipIcon } from '@phosphor-icons/react';

import { Button } from '../../../components/button/Button';
import { Pill } from '../../../components/pill/Pill';
import { useTranslation } from '../../../contexts/translation-context';
import { formatDate } from '../../../utils/date';
import { useDownloadDocument } from '../../document/hooks/useDownloadDocument';
import { FileUploaderButton } from '../../document/components/FileUploaderButton';
import type { IDocument } from '../../document/types';
import { TRUCK_DOCUMENT_STATUS_COLOR, type TruckDocumentStatus } from './truck-document-status';

const STATUS_LABEL_KEY = {
  valid: 'carrierPortal.trucks.statusValid',
  expiringSoon: 'carrierPortal.trucks.statusExpiringSoon',
  expired: 'carrierPortal.trucks.statusExpired',
  missing: 'carrierPortal.trucks.statusMissing',
} as const;

interface IMinimalFile {
  id: string;
  hash: string;
  name: string;
}

export interface IDocumentStatusRowProps {
  label: string;
  status: TruckDocumentStatus;
  /** Omitted when the document type carries no expiry date. */
  expiresAt?: Date | string | null;
  file?: IMinimalFile | null;
  /** Name suggested in the upload dialog. */
  uploadName: string;
  onUpload: (document: IDocument) => Promise<void>;
}

/**
 * One document inside a documents card — name, status, expiry, file and the
 * view / replace actions. Shared by the fleet and company information pages.
 */
export const DocumentStatusRow: React.FC<IDocumentStatusRowProps> = (props) => {
  const { label, status, expiresAt, file, uploadName, onUpload } = props;
  const { i18n } = useTranslation();
  const { downloadFile, downloadingFiles } = useDownloadDocument();

  return (
    <div className="flex flex-col gap-2.5 border-b border-border px-5 py-3.5 last:border-b-0">
      <div className="flex items-center gap-2">
        <FileTextIcon className="h-[15px] w-[15px] shrink-0 text-fg-muted" />
        <div className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">{label}</div>
        <Pill color={TRUCK_DOCUMENT_STATUS_COLOR[status]}>{i18n(STATUS_LABEL_KEY[status])}</Pill>
      </div>

      <div className="text-xs text-fg-secondary">
        {expiresAt
          ? i18n('carrierPortal.trucks.expiresOn', { date: formatDate(expiresAt) })
          : i18n('carrierPortal.trucks.noExpiryKnown')}
      </div>

      <div className="flex min-w-0 items-center gap-1.5">
        <PaperclipIcon className="h-3.5 w-3.5 shrink-0 text-fg-muted" />
        <span className="truncate text-2xs text-fg-muted">
          {file ? file.name : i18n('carrierPortal.trucks.notUploadedYet')}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {file && (
          <Button
            color="secondary"
            size={8}
            iconLeft={<EyeIcon className="button-icon" />}
            isLoading={downloadingFiles.includes(file.id)}
            onTrigger={() => downloadFile(file)}
          >
            {i18n('carrierPortal.trucks.viewDocument')}
          </Button>
        )}

        <FileUploaderButton
          size={8}
          buttonText={file ? i18n('carrierPortal.trucks.replaceDocument') : i18n('carrierPortal.trucks.uploadDocument')}
          title={label}
          initialName={uploadName}
          onSubmit={onUpload}
        />
      </div>
    </div>
  );
};
